import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { AHRService, ActionType, AuthenticatedIdentity, AuthenticationService, CaseFiles, CaseId, CurrentUser, Option, ReviewFile, ReviewerCase } from 'app/domain';
import { ConsoleService } from 'app/widgets/console.service';
import { saveAs } from 'file-saver';
import * as JSZip from 'jszip';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';
import { ReviewerService } from '../reviewer.service';

@Component({
    template: `
            <empty-state
                imgSrc="/assets/empty/theme.png"
                title="No case selected"
                description="Select a case to get started">
            </empty-state>
`
})
export class ReviewerCaseNotSelected {
    constructor(private reviewerService: ReviewerService) {
        reviewerService.updateWorkflowCaseId(null);
    }
}

@Component({
    selector: 'reviewer-case-list',
    templateUrl: './reviewer-case-list.component.html',
    styleUrls: ['./reviewer-case-list.component.scss']
})
export class ReviewerCaseListComponent implements OnInit, OnDestroy {
    @Input() selectedAHRYear!: string;
    cases: ReviewerCase[] = [];
    filteredCases: ReviewerCase[] = [];
    selectedCaseId: CaseId | null = null;
    loading = true;

    private destroy$ = new Subject<void>();
    currentUser: Option<CurrentUser> = Option.create<CurrentUser>();

    selectableCases: ReviewerCase[] = [];
    selAllAdv: boolean = false;
    selAllEvals: boolean = false;
    selectedFileCount: number = 0;
    readonly ADVANCEMENT_CASES: ActionType[] = [ActionType.Merit, ActionType.Promotion];
    readonly AE_CASES: ActionType[] = [ActionType.AnnualEvaluation];
    readonly selectableActionTypes: ActionType[] = [...this.ADVANCEMENT_CASES, ...this.AE_CASES];
   
    reviewerType: string = "AHR";
    shwSeparatedAcademics: boolean = false
    searchByName = new FormControl();
    reviewerRoles = new FormControl([]);
    myRoles: Set<String>;
    sortBy: 'firstName' | 'lastName' = 'firstName';
    sortDirection: 'asc' | 'desc' = 'asc';

    get selectedCaseIdForMenu() {
        return this.selectedCaseId;
    }

    set selectedCaseIdForMenu(id: CaseId | null) {
        console.log('selected case id:', id);
        let c = this.cases.filter(c => c.id.equals(id))[0];
        if (!!c) {
            this.router.navigate(['review', 'my-review', c.id.workflowCaseId]);
        } else {
            this.router.navigate(['review', 'my-review']);
        }
    }

    get isAHR(): boolean {
        return this.currentUser.value.roles.academicHumanResources;
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private svc: AHRService,
        private console: ConsoleService,
        private authsvc: AuthenticationService,
        private sb: MatSnackBar,
        private reviewerService: ReviewerService
    ) {

        authsvc.currentIdentity
            .pipe(takeUntil(this.destroy$))
            .subscribe(identity => {
                identity.match(
                    () => {
                        this.currentUser = Option.create<CurrentUser>();
                    },
                    (i: AuthenticatedIdentity) => {
                        this.currentUser = Option.create<CurrentUser>(i.user);
                    });
            });

        this.searchByName.valueChanges
            .pipe(
                startWith(''),
                debounceTime(200),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.filterCases();
            });
    }

    ngOnInit() {
        this.loadCases(this.selectedAHRYear);

        if (this.isAHR) {
            this.reviewerService.reviewer$
                .pipe(takeUntil(this.destroy$))
                .subscribe((type) => {
                    this.reviewerType = type;
                    this.selectedCaseId = null;
                    this.router.navigate(['/review/my-review']);
                    this.filterCases();
                });
        }

        this.reviewerService.currentWorkflowCaseId$
            .pipe(takeUntil(this.destroy$))
            .subscribe(caseId => {
                if (!!caseId) {
                    let c = this.cases.find(c => c.id.workflowCaseId === caseId);
                    if (!!c) this.selectedCaseId = c.id;
                } else {
                    this.selectedCaseId = null;
                }
            });

        this.reviewerRoles.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.filterCases();
            });
    }

    loadCases(ahryear: string) {
        combineLatest([
            this.svc.getCasesToBeReviewedCurrentUser(ahryear),
            this.route.data,
        ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([cases, data]) => {
                this.cases = cases;

                this.myRoles = new Set(
                    cases.map(r => r.yourSteps.map(s => s.title)).flat()
                        .filter(title => !!title) //filter out undefined/null
                        .sort((a, b) => a.localeCompare(b))
                );

                this.console.log('router data', data);

                if (!!data.autoSelectFirstCase && !this.selectedCaseId && cases.length >= 1) {
                    let c = cases[0];
                    let id = c.id;
                    this.console.log('selecting case', c);
                    this.router.navigate(['review', 'my-review', id.workflowCaseId]);
                }

                this.filterCases();

                this.loading = false;
            });
    }

    isSelectableCase(type: ActionType) {
        return this.selectableActionTypes.includes(type);
    }

    downloadSelectedCases() {

        let p = this.selectedFileCount > 1 ? 's' : '';
        this.sb.open('Downloading files from ' + this.selectedFileCount + ' case' + p, null, { duration: 6000 });

        let caseFileMap = new Map<ReviewerCase, CaseFiles>();

        var getCaseFiles = (_cases: ReviewerCase[]) => {

            const promiseArray = _cases.map(c => {

                return new Promise<Map<ReviewerCase, CaseFiles>>(resolve => {

                    this.svc.getCaseFiles(c.id, c.userId)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(files => {

                        let filesToDownload = files as CaseFiles;

                        // only show review files that a reviewer is allowed to see. See: AHRDashboardReadModel.canReadReviewFilesFromReviewStepIds for logic
                        filesToDownload.reviewerFiles = filesToDownload.reviewerFiles.filter(file =>
                            file.committeeName == "" ||
                            filesToDownload.canReadReviewFilesFromCommitteeNames.includes(file.committeeName)
                        );

                        caseFileMap.set(c, filesToDownload);
                        resolve(caseFileMap);
                    });
                });
            });

            return Promise.all(promiseArray);
        }

        const getCaseFilesPromise = getCaseFiles(this.selectableCases.filter(c => c.isChecked));

        Promise.all([getCaseFilesPromise]).then(results => {
            this.generateZip(caseFileMap);
        });

    }

    generateZip(_caseFileMap: Map<ReviewerCase, CaseFiles>) {

        var zip = new JSZip();

        var loadFiles = (_case: ReviewerCase, files: ReviewFile[], foldername: string) => {

            let filenames = [];

            zip.folder(_case.candidateName);

            const promiseArray = files.map((file, index) => {

                return new Promise(resolve => {
                    const fileName = file.id;
                    this.svc.caseFileObject(_case.id, fileName)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(response => {
                        var filename = response.headers.get('Content-Disposition').split("=")[1];
                        filename = filename.replace(/(^"|"$)/g, ''); //remove leading and trailing double quotes
                        filenames.push(filename);

                        //Rename duplicate files
                        let duplicateCount = filenames.filter(val => val == filename).length;
                        if (duplicateCount > 1) {
                            let fileExt = filename.substring(filename.lastIndexOf("."));
                            filename = filename.substring(0, filename.lastIndexOf("."));
                            filename = filename + "(" + duplicateCount + ")" + fileExt;
                            filenames.push(filename);
                        }
                        //this.console.log(filename);
                        const blob = new Blob([response.body], { type: response.body.type }); //create the file
                        zip.folder(_case.candidateName).folder(foldername).file(filename, blob, { binary: true }); //add the file to folder
                        resolve(response);
                    });
                });
            });

            return Promise.all(promiseArray);
        }

        const iterateCasesPromise = Array.from(_caseFileMap).map(([_case, files]) => {
            const candidatePromise = loadFiles(_case, files.candidateFiles, "Candidate Files");
            const reviewerPromise = loadFiles(_case, files.reviewerFiles, "Reviewer Files");
            return Promise.all([_case, candidatePromise, reviewerPromise]);
        });

        //Set zip filename
        const datepipe: DatePipe = new DatePipe('en-US');
        let formattedDate = datepipe.transform(new Date().toJSON(), 'YYYY.MM.dd_HH.mm.ss');
        let year = _caseFileMap.keys().next().value.id.periodOfReviewEndingInYear;
        let zipFilename = year + '-' + (year + 1) + " (" + formattedDate + ")";

        Promise.all(iterateCasesPromise).then(data => {
            zip.generateAsync({ type: "blob", compression: "DEFLATE" })
                .then(content => saveAs(content, zipFilename + '.zip'))
                .catch(err => console.log(err));
        }).catch((err) => {
            console.error(err);
        });

    }

    // Select or unselect all cases of the specified action types
    selectAllCases(actionTypes: ActionType[]) {
        this.selectableCases
            .filter(c => actionTypes.includes(c.proposedAction.type))
            .forEach(c => {
                //if all cases of this type are already selected, unselect all. Otherwise, select all.
                c.isChecked = (((this.AE_CASES.includes(c.proposedAction.type) && !this.selAllEvals) ||
                    (this.ADVANCEMENT_CASES.includes(c.proposedAction.type) && !this.selAllAdv)));
            });
        this.countChecked();
    }

    getVisibleAdvancementCases(): ReviewerCase[] {
        return this.selectableCases.filter(c =>
            this.ADVANCEMENT_CASES.includes(c.proposedAction.type)
        );
    }

    getVisibleEvaluationCases(): ReviewerCase[] {
        return this.selectableCases.filter(c =>
            this.AE_CASES.includes(c.proposedAction.type)
        );
    }

    countChecked() {
        //count the total of all checked cases for the download button
        this.selectedFileCount = 0;
        this.selectableCases.forEach(c => {
            if (c.isChecked) {
                this.selectedFileCount++;
            }
        });

        //Keeps track if all visible cases of each type are selected to update the select all button text
        const visibleAdvCases = this.getVisibleAdvancementCases();
        this.selAllAdv = visibleAdvCases.length > 0 && visibleAdvCases.every(c => c.isChecked == true);

        const visibleEvalCases = this.getVisibleEvaluationCases();
        this.selAllEvals = visibleEvalCases.length > 0 && visibleEvalCases.every(c => c.isChecked == true);
    }

    reviewerTypeChanged(reviewer: string) {
        this.reviewerService.updateReviewer(reviewer);
    }

    updateShowSeparatedAcademics() {
        this.shwSeparatedAcademics = !this.shwSeparatedAcademics;
        this.filterCases();
    }

    isSeparatedAcademic(c: ReviewerCase) {
        if (!c.separationDate) return false;
        const sepDate = new Date(c.separationDate);
        const today = new Date();
        return sepDate < today;
    }

    private getLastName(candidateName: string): string {
        const parts = candidateName.trim().split(' ');
        return parts.length > 1 ? parts[parts.length - 1] : candidateName;
    }

    private getFirstName(candidateName: string): string {
        const parts = candidateName.trim().split(' ');
        return parts[0];
    }

    toggleSort(sortType: 'firstName' | 'lastName') {
        if (this.sortBy === sortType) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortBy = sortType;
            this.sortDirection = 'asc';
        }
        this.filterCases();
    }

    private sortCases(cases: ReviewerCase[]): ReviewerCase[] {
        if (!this.sortBy) return cases;

        const sorted = [...cases].sort((a, b) => {
            let valueA: string;
            let valueB: string;

            if (this.sortBy === 'firstName') {
                valueA = this.getFirstName(a.candidateName).toLowerCase();
                valueB = this.getFirstName(b.candidateName).toLowerCase();
            } else {
                valueA = this.getLastName(a.candidateName).toLowerCase();
                valueB = this.getLastName(b.candidateName).toLowerCase();
            }

            const comparison = valueA.localeCompare(valueB);
            return this.sortDirection === 'asc' ? comparison : -comparison;
        });

        return sorted;
    }

    filterCases() {
        // Start with all cases
        let result = this.cases;

        // Apply role filter first
        const selectedRoles = this.reviewerRoles.value;
        if (selectedRoles && selectedRoles.length > 0) {
            result = result.filter(r =>
                r.yourSteps.some(s => selectedRoles.includes(s.title))
            );
        }

        // Search by name
        const searchTerm = this.searchByName.value;
        if (searchTerm) {
            result = result.filter(c => c.candidateName.toLowerCase().includes(searchTerm.toLowerCase()));
        }

        // If reviewer type is Reviewer, further filter to only show cases where the user is a reviewer
        result = this.reviewerType === 'Reviewer' ? result.filter(x => x.isReviewer) : result;

        // Filter based on separation status
        result = this.isAHR && this.shwSeparatedAcademics ? result : result.filter(c => !this.isSeparatedAcademic(c));

        this.filteredCases = result;

        // Apply sorting
        this.filteredCases = this.sortCases(this.filteredCases);

        // If the currently selected case is filtered out, select the first visible case or null if there are no visible cases
        if (!this.filteredCases || this.filteredCases.length === 0) {
            this.selectedCaseId = null;
            this.selectedCaseIdForMenu = null;
        } else if (this.selectedCaseId && !this.filteredCases.some(c => c.id.equals(this.selectedCaseId))) {
            this.selectedCaseId = this.filteredCases[0].id;
            this.selectedCaseIdForMenu = this.selectedCaseId; // This will trigger the navigation to the new case
        }

        if (this.isAHR) {
            if (!this.filteredCases || this.filteredCases.length === 0) {
                this.selectableCases = [];
            } else {
                // Update selectable cases based on filtered cases and selectable action types
                this.selectableCases = this.filteredCases.filter(c => c.proposedAction && this.isSelectableCase(c.proposedAction.type));
            }
            this.countChecked();
        }

    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

}